/** @type {string | null} */
let runtimeApiBase = null;

export function stripApiOrigin(url) {
  return String(url || "")
    .trim()
    .replace(/\/$/, "")
    .replace(/\/api$/i, "");
}

export function isLocalHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

export function setRuntimeApiBase(url) {
  runtimeApiBase = url ? stripApiOrigin(url) : null;
}

/**
 * Resolve API origin (no trailing slash, no /api suffix).
 * Request paths include `/api/...` (e.g. `/api/auth/login`).
 */
export function resolveApiBaseURL() {
  if (runtimeApiBase) return runtimeApiBase;

  const apiUrl = stripApiOrigin(process.env.REACT_APP_API_URL);
  const devUrl = stripApiOrigin(process.env.REACT_APP_DEV_API_URL);

  const isBrowser = typeof window !== "undefined";
  const hostname = isBrowser ? window.location.hostname : "";
  const isLocal = isLocalHostname(hostname);

  if (process.env.NODE_ENV === "development") {
    const useProxy = String(process.env.REACT_APP_USE_DEV_PROXY || "").trim() === "1";
    if (useProxy && isLocal) return "";
    return devUrl || "http://localhost:4000";
  }

  if (apiUrl) return apiUrl;

  const useSameOrigin =
    process.env.REACT_APP_API_USE_SAME_ORIGIN === "1" ||
    (isBrowser && /(^|\.)itsinception\.com$/i.test(hostname));

  if (isBrowser && useSameOrigin) {
    return window.location.origin;
  }

  if (isBrowser && isLocal) {
    return "http://localhost:4000";
  }

  return "";
}

export function getApiDisplayLabel() {
  const base = resolveApiBaseURL();
  if (base) return `${base}/api`;
  const useProxy = String(process.env.REACT_APP_USE_DEV_PROXY || "").trim() === "1";
  if (useProxy && typeof window !== "undefined") {
    const target =
      stripApiOrigin(process.env.REACT_APP_PROXY_TARGET) ||
      stripApiOrigin(process.env.REACT_APP_DEV_API_URL) ||
      "http://localhost:4000";
    return `${window.location.origin}/api → ${target}`;
  }
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api (not configured — see app-config.json)`;
  }
  return "not configured";
}

/**
 * HTTPS pages cannot call http://localhost:4000 (mixed content).
 */
export function getMixedContentWarning(apiBase) {
  if (typeof window === "undefined" || !apiBase) return null;
  if (window.location.protocol === "https:" && apiBase.startsWith("http:")) {
    return "This site is HTTPS but the API URL is HTTP. Browsers block that. For local development open http://localhost:3000 (npm start), not the production domain.";
  }
  return null;
}

/**
 * Load optional /app-config.json (no rebuild needed on static hosts).
 */
export async function loadRuntimeApiConfig() {
  if (typeof window === "undefined") return;
  try {
    const res = await fetch(`${window.location.origin}/app-config.json`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return;
    const data = await res.json();
    const base = stripApiOrigin(data.apiBaseUrl || data.apiUrl || data.API_URL);
    if (base) {
      setRuntimeApiBase(base);
      if (process.env.NODE_ENV === "development") {
        console.info("[API] runtime config", base);
      }
    }
  } catch {
    /* optional file */
  }
}

export function getMissingApiConfigMessage() {
  if (typeof window === "undefined") return "";
  const base = resolveApiBaseURL();
  if (base) return "";

  const host = window.location.hostname;

  if (process.env.NODE_ENV === "development" && isLocalHostname(host)) {
    const useProxy = String(process.env.REACT_APP_USE_DEV_PROXY || "").trim() === "1";
    if (useProxy) return "";
    return 'API URL is empty. Add REACT_APP_DEV_API_URL=http://localhost:4000 to .env.development and restart npm start, or set REACT_APP_USE_DEV_PROXY=1 with npm run server.';
  }

  if (
    process.env.REACT_APP_API_USE_SAME_ORIGIN === "1" ||
    /(^|\.)itsinception\.com$/i.test(host)
  ) {
    return "";
  }

  if (isLocalHostname(host)) {
    return 'API URL is empty. Set REACT_APP_API_URL or add public/app-config.json, then rebuild.';
  }

  return `No API URL is configured for "${host}". Add public/app-config.json with { "apiBaseUrl": "https://your-api-host" } (no /api suffix), or set REACT_APP_API_URL when running npm run build. For local work use http://localhost:3000 with npm run dev — not the live domain.`;
}
